import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import styles from "./EditAddressPanel.module.css";
import CrossButton from "../Customs/Button/CrossButton";
import WebService from "../Services/WebService";
import { toast } from "react-toastify";
import { FiMapPin, FiHome, FiGlobe } from "react-icons/fi";

export interface EditAddressPanelProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  initial: {
    address_id: number;
    user_id: number;
    address: string;
    landmark: string;
    country: number | "";
    state: number | "";
    city: number | "";
  };
  width?: number;

  /** NEW: where the panel is used.
   *  - "customer" => admin editing a customer's address (PUT /customers/address)
   *  - "self"     => user editing own address from cart/profile (POST /users/address)
   *  Defaults to "customer" so existing usages don’t change.
   */
  mode?: "customer" | "self";
}

type Country = { id: number; name: string };
type StateType = { id: number; name: string };
type City = { id: number; name: string };

const EditAddressPanel: React.FC<EditAddressPanelProps> = ({
  open,
  onClose,
  onSaved,
  initial,
  width = 480,
  mode = "customer",
}) => {
  const [form, setForm] = useState({ ...initial });
  const [saving, setSaving] = useState(false);

  const [countries, setCountries] = useState<Country[]>([]);
  const [states, setStates] = useState<StateType[]>([]);
  const [cities, setCities] = useState<City[]>([]);

  const [loadingCountries, setLoadingCountries] = useState(false);
  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [, setTouched] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (open) {
      setForm({ ...initial });
      setErrors({});
      setTouched({});
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initial.address_id]);

  useEffect(() => {
    if (!open) return;
    (async () => {
      setLoadingCountries(true);
      try {
        const res = await WebService.getAPI<any>({
          action: "/v1/door-management/users/countries",
        });
        const data = Array.isArray(res.data) ? res.data : res.data?.data || [];
        setCountries(data);
      } catch {
        setCountries([]);
      } finally {
        setLoadingCountries(false);
      }
    })();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (!form.country) {
      setStates([]);
      setForm((f) => ({ ...f, state: "", city: "" }));
      return;
    }
    (async () => {
      setLoadingStates(true);
      try {
        const res = await WebService.getAPI<any>({
          action: `/v1/door-management/users/countries/${form.country}/states`,
        });
        const data = Array.isArray(res.data) ? res.data : res.data?.data || [];
        setStates(data);
      } catch {
        setStates([]);
      } finally {
        setLoadingStates(false);
      }
    })();
  }, [open, form.country]);

  useEffect(() => {
    if (!open) return;
    if (!form.state) {
      setCities([]);
      setForm((f) => ({ ...f, city: "" }));
      return;
    }
    (async () => {
      setLoadingCities(true);
      try {
        const res = await WebService.getAPI<any>({
          action: `/v1/door-management/users/states/${form.state}/cities`,
        });
        const data = Array.isArray(res.data) ? res.data : res.data?.data || [];
        setCities(data);
      } catch {
        setCities([]);
      } finally {
        setLoadingCities(false);
      }
    })();
  }, [open, form.state]);

  const setField = <K extends keyof typeof form>(key: K, value: any) => {
    setForm((f) => ({ ...f, [key]: value }));
    setTouched((t) => ({ ...t, [String(key)]: true }));
  };

  const validate = () => {
    const e: Record<string, string> = {};
    if (!String(form.address || "").trim()) e.address = "Address required";
    if (!String(form.landmark || "").trim()) e.landmark = "Landmark required";
    if (!form.country) e.country = "Country required";
    if (!form.state) e.state = "State required";
    if (!form.city) e.city = "City required";
    return e;
  };

  const handleSave = async () => {
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length) return;

    setSaving(true);
    try {
      // payload common to both endpoints
      const basePayload = {
        address_id: form.address_id, // optional for upsert
        address_type: "GENERAL", // per your Postman examples
        address: form.address,
        landmark: form.landmark,
        city: Number(form.city),
        state: Number(form.state),
        country: Number(form.country),
      };

      if (mode === "self") {
        // Cart / self-edit => POST /users/address (upsert)
        await (WebService as any).postAPI({
          action: `/v1/door-management/users/address`,
          body: basePayload,
        });
      } else {
        // Customer edit => PUT /customers/address (requires user_id)
        await (WebService as any).putAPI({
          action: `/v1/door-management/customers/address`,
          body: {
            ...basePayload,
            user_id: form.user_id,
          },
        });
      }

      toast.success("Address saved successfully!");
      onSaved();
    } catch (err: any) {
      toast.error(err?.message || "Failed to save address");
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return createPortal(
    <div className={styles.overlay} onClick={onClose}>
      <div
        className={styles.panel}
        style={{ width }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className={styles.header}>
          <h3 className={styles.title}>Edit Address</h3>
          <CrossButton onClick={onClose} />
        </div>

        {/* Body */}
        <div className={styles.form}>
          {/* Address */}
          <div className={styles.inputGroup}>
            <label className={styles.label}>Address</label>
            <div className={styles.inputWithIcon}>
              <FiMapPin className={styles.icon} />
              <input
                className={`${styles.input} ${errors.address ? styles.inputError : ""}`}
                value={form.address}
                onChange={(e) => setField("address", e.target.value)}
                placeholder="e.g. Street address"
              />
            </div>
            {errors.address && <div className={styles.error}>{errors.address}</div>}
          </div>

          {/* Landmark + Country */}
          <div className={styles.row}>
            <div className={styles.inputGroup}>
              <label className={styles.label}>Landmark</label>
              <div className={styles.inputWithIcon}>
                <FiHome className={styles.icon} />
                <input
                  className={`${styles.input} ${errors.landmark ? styles.inputError : ""}`}
                  value={form.landmark}
                  onChange={(e) => setField("landmark", e.target.value)}
                  placeholder="e.g. Near City Mall"
                />
              </div>
              {errors.landmark && <div className={styles.error}>{errors.landmark}</div>}
            </div>

            <div className={styles.inputGroup}>
              <label className={styles.label}>Country</label>
              <div className={styles.inputWithIcon}>
                <FiGlobe className={styles.icon} />
                <select
                  className={`${styles.select} ${errors.country ? styles.inputError : ""}`}
                  value={form.country}
                  onChange={(e) => {
                    const val = e.target.value ? Number(e.target.value) : "";
                    setForm((f) => ({ ...f, country: val, state: "", city: "" }));
                  }}
                >
                  <option value="">{loadingCountries ? "Loading..." : "Select Country"}</option>
                  {countries.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
              {errors.country && <div className={styles.error}>{errors.country}</div>}
            </div>
          </div>

          {/* State + City */}
          <div className={styles.row}>
            <div className={styles.inputGroup}>
              <label className={styles.label}>State</label>
              <div className={styles.inputWithIcon}>
                <FiMapPin className={styles.icon} />
                <select
                  className={`${styles.select} ${errors.state ? styles.inputError : ""}`}
                  value={form.state}
                  onChange={(e) => {
                    const val = e.target.value ? Number(e.target.value) : "";
                    setForm((f) => ({ ...f, state: val, city: "" }));
                  }}
                  disabled={!form.country}
                >
                  <option value="">{loadingStates ? "Loading..." : "Select State"}</option>
                  {states.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </div>
              {errors.state && <div className={styles.error}>{errors.state}</div>}
            </div>

            <div className={styles.inputGroup}>
              <label className={styles.label}>City</label>
              <div className={styles.inputWithIcon}>
                <FiMapPin className={styles.icon} />
                <select
                  className={`${styles.select} ${errors.city ? styles.inputError : ""}`}
                  value={form.city}
                  onChange={(e) => {
                    const val = e.target.value ? Number(e.target.value) : "";
                    setField("city", val);
                  }}
                  disabled={!form.state}
                >
                  <option value="">{loadingCities ? "Loading..." : "Select City"}</option>
                  {cities.map((ci) => (
                    <option key={ci.id} value={ci.id}>
                      {ci.name}
                    </option>
                  ))}
                </select>
              </div>
              {errors.city && <div className={styles.error}>{errors.city}</div>}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className={styles.footer}>
          <button
            type="button"
            className={styles.cancelBtn}
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="button"
            className={styles.saveBtn}
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? "Saving..." : "Save Address"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default EditAddressPanel;
